import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Save, Upload, ShieldCheck, Power } from "lucide-react";
import { toast } from "sonner";

export default function OrgSettings() {
  const { user, loadOrg } = useAuth();
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/organization").then(({ data }) => setOrg(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Logo 1 MB'tan küçük olmalı."); return; }
    const reader = new FileReader();
    reader.onload = () => setOrg({ ...org, logo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/organization", {
        full_title: org.full_title, short_name: org.short_name, logo: org.logo, watermark: org.watermark,
        address: org.address, phone: org.phone, email: org.email, website: org.website,
      });
      toast.success("Kuruluş ayarları kaydedildi.");
      await loadOrg();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleOrg = async () => {
    try {
      await api.post(`/organization/${org.is_active ? "deactivate" : "activate"}`);
      toast.success(org.is_active ? "Kuruluş pasife alındı." : "Kuruluş yeniden açıldı.");
      await loadOrg();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!org) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-display text-2xl font-bold text-slate-900">Kuruluş Ayarları</h1>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-display text-lg">Marka & İletişim</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-4">
            {org.logo ? <img src={org.logo} alt="logo" className="h-16 w-16 rounded-xl object-cover border border-slate-200" /> :
              <div className="h-16 w-16 rounded-xl bg-sky-600 flex items-center justify-center"><ShieldCheck className="h-8 w-8 text-white" /></div>}
            <div>
              <input type="file" accept="image/*" id="logo-input" className="hidden" onChange={onLogo} data-testid="org-settings-logo-upload" />
              <Button variant="outline" onClick={() => document.getElementById("logo-input").click()}><Upload className="h-4 w-4 mr-2" /> Logo Yükle</Button>
              <p className="text-xs text-slate-400 mt-1">PNG/JPG, en fazla 1 MB. Üst barda ve müşteri ekranında görünür.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2"><Label>Tam Unvan</Label><Input value={org.full_title || ""} onChange={(e) => setOrg({ ...org, full_title: e.target.value })} data-testid="org-full-title-input" /></div>
            <div className="space-y-1.5"><Label>Kısa Ad</Label><Input value={org.short_name || ""} onChange={(e) => setOrg({ ...org, short_name: e.target.value })} data-testid="org-short-name-input" /></div>
            <div className="space-y-1.5"><Label>Web Sitesi</Label><Input value={org.website || ""} onChange={(e) => setOrg({ ...org, website: e.target.value })} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Adres</Label><Input value={org.address || ""} onChange={(e) => setOrg({ ...org, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Telefon</Label><Input value={org.phone || ""} onChange={(e) => setOrg({ ...org, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>E-posta</Label><Input value={org.email || ""} onChange={(e) => setOrg({ ...org, email: e.target.value })} /></div>
          </div>
          <Button onClick={save} className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="org-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-2" /> Kaydet
          </Button>
        </CardContent>
      </Card>

      {user.role === "main_admin" && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display text-lg">Kuruluş Durumu (Yalnızca Ana Yönetici)</CardTitle></CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><span className="text-sm text-slate-600">Mevcut durum:</span><StatusBadge tone={org.is_active ? "active" : "passive"}>{org.is_active ? "Aktif" : "Pasif"}</StatusBadge></div>
              <p className="text-xs text-slate-400 mt-1 max-w-md">Kuruluş pasifteyken ana yönetici dışındaki tüm kullanıcılar erişimini kaybeder; veriler silinmez.</p>
            </div>
            <Button variant="outline" onClick={toggleOrg} className={org.is_active ? "text-rose-600 border-rose-200" : "text-emerald-600 border-emerald-200"} data-testid="org-toggle-active">
              <Power className="h-4 w-4 mr-2" /> {org.is_active ? "Kuruluşu Pasife Al" : "Kuruluşu Yeniden Aç"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
